import axios from 'axios';

const horseApiUrl = process.env.REACT_APP_HORSE_API_URL || 'https://testeint.apihorse.nexxusone.com.br';
const horseApiKey = process.env.REACT_APP_HORSE_API_KEY;

const HorseCheckout = axios.create({
  baseURL: horseApiUrl,
});

export interface BoletoPagadorPayload {
  'cpf-cnpj': string;
  nome: string;
  telefone: string;
  email: string;
  cep: string;
  endereco: string;
  numero: string;
  complemento?: string;
  bairro: string;
  estado: string;
  cidade: string;
}

export interface BoletoPayload {
  metodo: 'boleto';
  valor: number;
  parcelas: number;
  'primeiro-vencimento': string;
  pagador: BoletoPagadorPayload;
}

export interface BoletoTitulo {
  titulo_id: string;
  status: string;
  nosso_numero: string;
  linha_digitavel: string;
  codigo_barras: string;
  pdf_base64: string;
  motivo?: string;
  parcela?: number;
  vencimento?: string;
  valor?: number;
  cobranca_id?: string;
}

export interface BoletoItemErro {
  parcela?: number;
  motivo: string;
}

export interface EmitirBoletoResult {
  pagador_id?: string;
  valor_total?: number;
  parcelas?: number;
  /** Formato legado (fila) */
  instrucao?: string;
  enfileirados?: number;
  ignorados?: number;
  titulos: BoletoTitulo[];
  erros: BoletoItemErro[];
}

function isBoletoErroStatus(status: string): boolean {
  const s = status.trim().toLowerCase();
  return s === 'erro' || s === 'error' || s === 'falha' || s === 'failed';
}

function normalizeBoletoTitulo(item: Record<string, unknown>): BoletoTitulo {
  const motivoRaw = item.motivo ?? item.Motivo ?? item.erro_mensagem ?? item.erroMensagem;
  return {
    titulo_id: String(item.titulo_id ?? item.tituloId ?? ''),
    status: String(item.status ?? ''),
    nosso_numero: String(item.nosso_numero ?? item.nossoNumero ?? ''),
    linha_digitavel: String(item.linha_digitavel ?? item.linhaDigitavel ?? ''),
    codigo_barras: String(item.codigo_barras ?? item.codigoBarras ?? ''),
    pdf_base64: String(item.pdf_base64 ?? item.pdfBase64 ?? ''),
    motivo: motivoRaw ? String(motivoRaw) : undefined,
    parcela: typeof item.parcela === 'number' ? item.parcela : undefined,
    vencimento: item.vencimento ? String(item.vencimento) : undefined,
    valor: typeof item.valor === 'number' ? item.valor : undefined,
    cobranca_id: item.cobranca_id ? String(item.cobranca_id) : item.cobrancaId ? String(item.cobrancaId) : undefined,
  };
}

function formatBoletoErros(erros: BoletoItemErro[]): string {
  if (erros.length === 1) return erros[0].motivo;
  return erros
    .map((e) => (e.parcela != null ? `Parcela ${e.parcela}: ${e.motivo}` : e.motivo))
    .join(' | ');
}

function splitBoletosResponse(dados: Record<string, unknown>): {
  titulos: BoletoTitulo[];
  erros: BoletoItemErro[];
} {
  const boletos = Array.isArray(dados.boletos) ? dados.boletos : [];
  const titulosLegado = Array.isArray(dados.titulos) ? dados.titulos : [];
  const raw = boletos.length > 0 ? boletos : titulosLegado;

  const titulos: BoletoTitulo[] = [];
  const erros: BoletoItemErro[] = [];

  for (const item of raw) {
    const normalized = normalizeBoletoTitulo(item as Record<string, unknown>);

    if (isBoletoErroStatus(normalized.status)) {
      erros.push({
        parcela: normalized.parcela,
        motivo: normalized.motivo?.trim() || 'Falha ao gerar boleto.',
      });
      continue;
    }

    if (normalized.titulo_id || normalized.linha_digitavel || normalized.pdf_base64) {
      titulos.push(normalized);
    } else if (normalized.motivo?.trim()) {
      erros.push({
        parcela: normalized.parcela,
        motivo: normalized.motivo.trim(),
      });
    }
  }

  return { titulos, erros };
}

function getErrorMessage(error: any): string {
  const erro = error.response?.data?.erro;
  if (erro?.mensagem) return erro.mensagem;
  if (error.response?.data?.message) return error.response.data.message;
  return error.message || 'Erro ao emitir boleto';
}

export async function emitirBoleto(
  linkPagamentoId: string,
  payload: BoletoPayload
): Promise<EmitirBoletoResult> {
  if (!horseApiKey) {
    throw new Error('REACT_APP_HORSE_API_KEY não configurada no .env');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': horseApiKey,
    'x-link': linkPagamentoId,
  };

  try {
    const response = await HorseCheckout.post('/link-pagamento/pagar?=', payload, { headers });

    const raw = response.data;
    const dados = raw?.dados;
    if (!raw?.sucesso || !dados) {
      throw new Error(getErrorMessage({ response: { data: raw } }) || 'Resposta inválida da API Horse.');
    }

    const { titulos, erros } = splitBoletosResponse(dados);

    if (titulos.length === 0 && erros.length > 0) {
      throw new Error(formatBoletoErros(erros));
    }

    if (titulos.length === 0) {
      throw new Error('Nenhum boleto foi gerado pela API.');
    }

    return {
      pagador_id: dados.pagador_id ?? dados.pagadorId,
      valor_total: dados.valor_total ?? dados.valorTotal,
      parcelas: dados.parcelas,
      instrucao: dados.instrucao,
      enfileirados: dados.enfileirados,
      ignorados: dados.ignorados,
      titulos,
      erros,
    };
  } catch (error: any) {
    console.error('Erro ao emitir boleto:', error.message);
    throw new Error(getErrorMessage(error));
  }
}
